import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { FaHourglassHalf, FaSearch, FaCopy } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DescriptionViewer from '../../Common/DescriptionViewer';

const AcceptedReporterAdvertisement = () => {
  // Pagination state for ads list
  const [currentPage, setCurrentPage] = useState(1);
  const adsPerPage = 50;
  const [ads, setAds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [previewType, setPreviewType] = useState("");

  const handleMediaClick = (mediaUrl, mediaType) => {
    setPreviewMedia(mediaUrl);
    setPreviewType(mediaType);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewMedia(null);
  };

  const MediaPreviewModal = () => {
    if (!showPreview) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
        <div className="relative max-w-4xl max-h-[90vh] overflow-auto p-4">
          <button
            onClick={closePreview}
            className="absolute top-2 right-2 bg-white rounded-full p-2 text-gray-800 hover:text-gray-900 z-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          {previewType.toLowerCase() === "video" ? (
            <video
              src={previewMedia}
              controls
              autoPlay
              className="max-h-[80vh] max-w-full"
            />
          ) : (
            <img
              src={previewMedia}
              alt="Preview"
              className="max-h-[80vh] max-w-full"
            />
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/ads/accepted/by/reporters/for/advertiser`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched accepted reporter ads:", response.data);
        // Ensure ads is always an array
        const adsData =
          Array.isArray(response.data.data) && response.data.data.length > 0
            ? response.data.data
            : Array.isArray(response.data) && response.data.length > 0
              ? response.data
              : [];
        setAds(adsData);
      } catch (err) {
        setError("Failed to fetch ads");
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, []);

  // Filter logic for search bar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );


  const filteredAds = ads.filter((ad) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();

    // Search by IINSAF ID format
    if (/^iinsaf\d+$/i.test(searchTerm.trim())) {
      return (
        ad.acceptedReporters &&
        ad.acceptedReporters.some((reporter) => {
          return (
            reporter.iinsafId &&
            reporter.iinsafId.toString().toLowerCase() === term
          );
        })
      );
    }

    // Search by ad details
    if (
      (ad.adId && ad.adId.toString().toLowerCase().includes(term)) ||
      (ad._id && ad._id.toString().toLowerCase().includes(term)) ||
      (ad.adTitle && ad.adTitle.toLowerCase().includes(term))
    ) {
      return true;
    }

    // Search by reporter details
    if (
      ad.acceptedReporters &&
      ad.acceptedReporters.some((reporter) => {
        return (
          (reporter.iinsafId &&
            reporter.iinsafId.toString().toLowerCase().includes(term)) ||
          (reporter.reporterId &&
            reporter.reporterId.toString().toLowerCase().includes(term)) ||
          (reporter.rejectNote &&
            reporter.rejectNote.toLowerCase().includes(term)) ||
          (reporter.acceptedAt &&
            new Date(reporter.acceptedAt)
              .toLocaleDateString("en-GB")
              .toLowerCase()
              .includes(term))
        );
      })
    ) {
      return true;
    }

    return false;
  });

  // Calculate total pages for ads list
  // Sort ads by most recent accepted reporter's acceptedAt time (hh:mm:ss)
  const sortedAds = [...filteredAds].sort((a, b) => {
    // Get latest acceptedAt for each ad
    const getLatestTime = (ad) => {
      if (!ad.acceptedReporters || ad.acceptedReporters.length === 0) return 0;
      return Math.max(...ad.acceptedReporters
        .filter(r => r.acceptedAt)
        .map(r => new Date(r.acceptedAt).getTime()));
    };
    return getLatestTime(b) - getLatestTime(a);
  });
  const totalPages = Math.ceil(sortedAds.length / adsPerPage);

  // Handle page change for ads list
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      <MediaPreviewModal />
      <div className="flex flex-col mb-6 gap-4 mt-2">
        <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">
          Accepted Reporter Advertisements
        </h1>
        {/* Floating Pill Bar for Desktop & Mobile Navigation */}
        <div className="bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-200/40 border border-slate-100 rounded-[1.5rem] sm:rounded-full p-1.5 sm:p-2 w-full mx-auto pointer-events-auto transition-all">
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-1.5 sm:gap-2 w-full">
            {[
              { path: '/advertiser/leads-status/underReview', label: 'Under Review', activeColor: 'bg-yellow-500', activeShadow: 'shadow-yellow-500/30' },
              { path: '/advertiser/leads-status/pending', label: 'Approved', activeColor: 'bg-blue-500', activeShadow: 'shadow-blue-500/30' },
              { path: '/advertiser/leads-status/accepted', label: 'Accepted', activeColor: 'bg-teal-500', activeShadow: 'shadow-teal-500/30', active: true },
              { path: '/advertiser/leads-status/running', label: 'Running', activeColor: 'bg-emerald-500', activeShadow: 'shadow-emerald-500/30' },
              { path: '/advertiser/leads-status/completed', label: 'Completed', activeColor: 'bg-indigo-500', activeShadow: 'shadow-indigo-500/30' },
              { path: '/advertiser/leads-status/rejected', label: 'Rejected', activeColor: 'bg-rose-500', activeShadow: 'shadow-rose-500/30' }
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                className={`
                  flex-1 sm:flex-none flex items-center justify-center px-2 sm:px-4 py-2 sm:py-2.5 
                  rounded-xl sm:rounded-full transition-all duration-300 text-[10px] sm:text-xs font-black uppercase tracking-widest text-center whitespace-nowrap
                  ${item.active
                    ? `${item.activeColor} text-white shadow-md ${item.activeShadow} scale-[1.02]`
                    : 'bg-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700 active:scale-95'
                  }
                `}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4 sm:mb-6">
        <div className="relative max-w-2xl mx-auto">
          <input
            type="text"
            placeholder="Search by Advertisement ID, IINSAF ID, Reporter ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 sm:py-3.5 rounded-2xl border-2 border-slate-100 bg-white focus:outline-none focus:ring-4 focus:ring-yellow-500/10 focus:border-yellow-500 transition-all text-sm sm:text-base shadow-sm font-medium text-slate-700 placeholder:text-slate-400"
          />
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
        </div>
      </div>

      {ads.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <h3 className="text-xl font-semibold text-gray-700">
            No Accepted Advertisements
          </h3>
          <p className="text-gray-500">
            You don't have any accepted advertisements at the moment.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            {sortedAds
              .slice((currentPage - 1) * adsPerPage, currentPage * adsPerPage)
              .map((ad) => (
                <div key={ad._id} className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                  <div className="px-1 py-1">
                    <div className="flex flex-col sm:flex-row justify-between bg-gradient-to-r from-blue-600 to-indigo-600 text-white items-start sm:items-center p-3 sm:p-4 mb-2 sm:mb-4 rounded-lg">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm sm:text-lg font-bold tracking-tight">
                            Ad ID: {ad.adId || ad._id}
                          </h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(ad.adId || ad._id);
                              toast.success('ID Copied!');
                            }}
                            className="bg-white/20 hover:bg-white/30 text-white rounded p-1 transition-colors"
                            title="Copy ID"
                          >
                            <FaCopy className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                        <p className="text-xs sm:text-sm font-medium text-blue-100 mt-0.5">
                          Total Accepted: <span className="font-bold text-white">{ad.acceptedCount || ad.acceptedReporters?.length || 0}</span>
                        </p>
                      </div>

                      {/* Location Information block */}
                      <div className="mt-3 sm:mt-0 flex flex-wrap gap-2 text-xs w-full sm:w-auto">
                        <div className="bg-white/10 px-3 py-1.5 rounded-lg border border-white/20 min-w-[120px]">
                          <p className="text-[9px] font-bold text-blue-200 uppercase tracking-widest mb-0.5">Original Target</p>
                          <p className="font-bold">{ad.pfState || "Not specified"}</p>
                          <p className="text-[10px] text-blue-100 max-w-[150px] truncate" title={ad.pfCities?.join(", ")}>
                            {ad.pfCities && ad.pfCities.length > 0 ? ad.pfCities.join(", ") : "All Cities"}
                          </p>
                        </div>

                        {(ad.adminSelectState || (ad.adminSelectCities && ad.adminSelectCities.length > 0)) && (
                          <div className="bg-emerald-500/20 px-3 py-1.5 rounded-lg border border-emerald-400/30 min-w-[120px]">
                            <p className="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-0.5">Admin Modified</p>
                            <p className="font-bold text-emerald-50">{ad.adminSelectState || "Multi-State"}</p>
                            <p className="text-[10px] text-emerald-100/90 max-w-[150px] truncate" title={ad.adminSelectCities?.join(", ")}>
                              {ad.adminSelectCities && ad.adminSelectCities.length > 0 ? ad.adminSelectCities.join(", ") : "All Cities"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="min-w-full rounded-lg mt-0 text-xs sm:text-sm">
                        <thead className="bg-gray-50 border border-slate-200">
                          <tr>
                            <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider border-b">
                              Media
                            </th>
                            <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider border-b">
                              IINSAF ID
                            </th>
                            <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider border-b">
                              Title
                            </th>
                            <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider border-b">
                              Description
                            </th>
                            <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider border-b">
                              Status
                            </th>
                            <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider border-b">
                              Ad Proof
                            </th>
                            <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider border-b">
                              Accepted At
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-100 border border-slate-200 border-t-0">
                          {ad.acceptedReporters &&
                            ad.acceptedReporters.length > 0 ? (
                            ad.acceptedReporters.map((reporter, idx) => {
                              const d = reporter.acceptedAt
                                ? new Date(reporter.acceptedAt)
                                : null;
                              let dateStr = "-",
                                timeStr = "-";
                              if (d) {
                                const day = String(d.getDate()).padStart(2, "0");
                                const month = String(d.getMonth() + 1).padStart(
                                  2,
                                  "0"
                                );
                                const year = d.getFullYear();
                                dateStr = `${day}-${month}-${year}`;
                                timeStr = d.toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                });
                              }
                              return (
                                <tr key={reporter._id || idx} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2 border-r border-slate-100">
                                    {ad.videoUrl ? (
                                      <video
                                        src={ad.videoUrl}
                                        className="w-10 h-10 object-cover rounded-lg cursor-pointer shadow-sm border border-slate-200"
                                        onClick={() =>
                                          handleMediaClick(ad.videoUrl, "video")
                                        }
                                      />
                                    ) : ad.imageUrl ? (
                                      <img
                                        src={ad.imageUrl}
                                        alt={ad.adTitle || "Ad"}
                                        className="w-10 h-10 object-cover rounded-lg cursor-pointer shadow-sm border border-slate-200"
                                        onClick={() =>
                                          handleMediaClick(ad.imageUrl, "image")
                                        }
                                      />
                                    ) : (
                                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200">
                                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                                          N/A
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2 font-bold text-slate-800 text-[10px] sm:text-xs border-r border-slate-100">
                                    {reporter.iinsafId || "N/A"}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2 text-slate-600 border-r border-slate-100">
                                    <DescriptionViewer
                                      description={ad.adTitle || ""}
                                      wordLimit={5}
                                      textSize="text-[10px] sm:text-xs"
                                      buttonText="View Title"
                                      modalTitle="Title"
                                    />
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2 text-slate-600 border-r border-slate-100">
                                    <DescriptionViewer
                                      description={ad.mediaDescription || ""}
                                      wordLimit={5}
                                      textSize="text-[10px] sm:text-xs"
                                    />
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2 border-r border-slate-100">
                                    <span
                                      className={`px-2 py-1 rounded-md text-[9px] sm:text-xs font-black uppercase tracking-wider ${reporter.accepted === true
                                        ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                                        : "bg-rose-50 text-rose-600 border border-rose-100"
                                        }`}
                                    >
                                      {reporter.accepted === true
                                        ? "Accepted"
                                        : "Rejected"}
                                    </span>
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2 border-r border-slate-100">
                                    <span
                                      className={`px-2 py-1 rounded-md text-[9px] sm:text-xs font-black uppercase tracking-wider ${reporter.adProof === true
                                        ? "bg-blue-50 text-blue-600 border border-blue-100"
                                        : "bg-slate-100 text-slate-500 border border-slate-200"
                                        }`}
                                    >
                                      {reporter.adProof === true
                                        ? "Submitted"
                                        : "Pending"}
                                    </span>
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2">
                                    <div className="flex flex-col">
                                      <span className="text-[10px] sm:text-xs font-bold text-slate-700">{dateStr}</span>
                                      <span className="text-[10px] font-mono text-slate-500">
                                        {timeStr}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={8}
                                className="px-4 py-4 text-center text-slate-500 font-medium"
                              >
                                No accepted reporters
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View Structure */}
                    <div className="lg:hidden flex flex-col gap-3 px-1">
                      {ad.acceptedReporters && ad.acceptedReporters.length > 0 ? (
                        ad.acceptedReporters.map((reporter, idx) => {
                          const d = reporter.acceptedAt ? new Date(reporter.acceptedAt) : null;
                          let dateStr = "-", timeStr = "-";
                          if (d) {
                            const day = String(d.getDate()).padStart(2, "0");
                            const month = String(d.getMonth() + 1).padStart(2, "0");
                            const year = d.getFullYear();
                            dateStr = `${day}-${month}-${year}`;
                            timeStr = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
                          }

                          return (
                            <div key={reporter._id || idx} className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 flex flex-col gap-3 relative">
                              <div className="flex items-start justify-between border-b border-slate-100 pb-2">
                                <div className="flex gap-2.5 items-center">
                                  {/* Media Thumbnail */}
                                  {ad.videoUrl ? (
                                    <video src={ad.videoUrl} className="w-12 h-12 rounded-lg object-cover cursor-pointer shadow-sm border border-slate-200" onClick={() => handleMediaClick(ad.videoUrl, "video")} />
                                  ) : ad.imageUrl ? (
                                    <img src={ad.imageUrl} alt={ad.adTitle || "Ad"} className="w-12 h-12 rounded-lg object-cover cursor-pointer shadow-sm border border-slate-200" onClick={() => handleMediaClick(ad.imageUrl, "image")} />
                                  ) : (
                                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200"><span className="text-[10px] font-bold text-slate-400">N/A</span></div>
                                  )}

                                  <div className="flex flex-col">
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Reporter ID</span>
                                    <span className="text-xs font-bold text-slate-800">{reporter.iinsafId || "N/A"}</span>
                                  </div>
                                </div>

                                <div className="flex flex-col items-end gap-1">
                                  <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${reporter.accepted ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-rose-50 text-rose-600 border border-rose-100"}`}>
                                    {reporter.accepted ? "Accepted" : "Rejected"}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${reporter.adProof ? "bg-blue-50 text-blue-600 border border-blue-100" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                                    {reporter.adProof ? "Submitted" : "Pending"}
                                  </span>
                                </div>
                              </div>

                              <div className="flex flex-col gap-2">
                                <div>
                                  <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Ad Title</div>
                                  <DescriptionViewer description={ad.adTitle || ""} wordLimit={6} textSize="text-xs text-slate-700" buttonText="View" modalTitle="Title" />
                                </div>
                                <div>
                                  <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Description</div>
                                  <DescriptionViewer description={ad.mediaDescription || ""} wordLimit={6} textSize="text-xs text-slate-700" />
                                </div>
                              </div>

                              <div className="flex items-center justify-between mt-1 pt-2 border-t border-slate-100">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Accepted On</span>
                                <div className="flex gap-1 items-center bg-slate-50 px-2.5 py-1 rounded-md border border-slate-200">
                                  <span className="text-[10px] font-bold text-slate-700">{dateStr}</span>
                                  <span className="text-slate-300">|</span>
                                  <span className="text-[10px] font-mono text-slate-600">{timeStr}</span>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      ) : (
                        <div className="text-center italic text-xs text-slate-500 py-4 bg-slate-50 rounded-xl border border-slate-100">No accepted reporters found.</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* Show empty state if nothing matches search but list is not entirely empty overall */}
          {ads.length > 0 && filteredAds.length === 0 && (
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center text-slate-500 mt-4 border border-gray-200">
              No advertisements found matching "{searchTerm}"
            </div>
          )}

          {/* Pagination Controls for ads list */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <nav className="inline-flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx + 1}
                    onClick={() => handlePageChange(idx + 1)}
                    className={`px-3 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === idx + 1 ? 'bg-yellow-300 text-yellow-900 font-bold' : ''}`}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AcceptedReporterAdvertisement;
